import {
  CountryCode,
  codeFromEnglishName,
  countryKeyFromCode,
  getEnglishNameFromCode,
} from '../constants/Countries.ts';
import { IManifestCountry } from '../services/ManifestService.ts';

export interface ManifestCountryCodeOption {
  name: string;
  value: CountryCode;
}

export interface ManifestTelephoneCountryOption {
  name: string;
  value: CountryCode;
  label: string;
  code: number;
}

const isCountryCode = (value: string): value is CountryCode =>
  Object.values(CountryCode).includes(value as CountryCode);

export const toManifestCountryCodeOptions = (
  countries: IManifestCountry[]
): ManifestCountryCodeOption[] =>
  countries
    .map((country) => ({
      name: country.name,
      value: country.phone.value,
    }))
    .filter((country): country is ManifestCountryCodeOption => isCountryCode(country.value));

export const toLocalizedAleaCountryOptions = (
  countries: ManifestCountryCodeOption[],
  translate: (key: string) => string
) =>
  countries.map((country) => {
    const translationKey = countryKeyFromCode(country.value);
    const translated = translate(translationKey);

    return {
      value: country.value,
      // Keep manifest name as fallback when translation key is missing.
      label: translated === translationKey ? country.name : translated,
    };
  });

export const toLocalizedTelephoneCountryOptions = (
  countries: IManifestCountry[],
  translate: (key: string) => string
): ManifestTelephoneCountryOption[] => {
  const phoneByCode = countries.reduce<
    Partial<Record<CountryCode, Pick<ManifestTelephoneCountryOption, 'label' | 'code'>>>
  >((acc, country) => {
    const code = country.phone.value;
    if (!isCountryCode(code)) return acc;
    acc[code] = {
      label: country.phone.label,
      code: country.phone.code,
    };
    return acc;
  }, {});

  return toManifestCountryCodeOptions(countries)
    .map((country) => {
      const translationKey = countryKeyFromCode(country.value);
      const translated = translate(translationKey);
      const phone = phoneByCode[country.value];

      if (!phone) return null;

      return {
        value: country.value,
        name: translated === translationKey ? country.name : translated,
        label: phone.label,
        code: phone.code,
      };
    })
    .filter((country): country is ManifestTelephoneCountryOption => !!country);
};

export const resolveCountryCode = (
  countryName: string | undefined | null,
  countries: ManifestCountryCodeOption[]
): CountryCode | '' => {
  if (!countryName) return '';

  const manifestMatch = countries.find(
    (country) => country.name.toLowerCase() === countryName.toLowerCase()
  );
  if (manifestMatch) return manifestMatch.value;

  return codeFromEnglishName(countryName) || '';
};

export const resolveCountryNameForPayload = (
  countryCode: CountryCode | '',
  countries: ManifestCountryCodeOption[],
  fallbackCode: CountryCode = CountryCode.DE
): string => {
  const countryNameByCode = countries.reduce<Record<CountryCode, string>>((acc, country) => {
    acc[country.value] = country.name;
    return acc;
  }, {} as Record<CountryCode, string>);

  if (countryCode) {
    return countryNameByCode[countryCode] || getEnglishNameFromCode(countryCode);
  }

  return countryNameByCode[fallbackCode] || getEnglishNameFromCode(fallbackCode);
};
